import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AvatarIcon, CountryFlag } from "@/lib/identity";
import { Lightning, Trophy, Users, Crown, Sparkle, Hourglass, ArrowRight, Gauge } from "@phosphor-icons/react";

export default function AdminDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    const tick = () => {
      api.get("/admin/dashboard").then((r) => { if (alive) setData(r.data); }).catch(() => {});
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (!data) {
    return (
      <div className="hidden lg:block">
        <Heading season={null} />
        <p className="text-white/60 text-sm">Loading command center…</p>
      </div>
    );
  }

  const s = data.season || {};
  const m = data.live_metrics || {};
  const cw = data.country_war || {};
  const t = data.today || {};
  const up = data.upcoming || {};

  return (
    <div className="space-y-8">
      <div className="hidden lg:block"><Heading season={s} /></div>

      {/* Season status banner */}
      <SeasonBanner season={s} top={data.top_player} />

      {/* Primary live metrics */}
      <section>
        <p className="eyebrow mb-3">Live metrics</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Metric Icon={Users} label="Players online" value={m.players_online} accent="text-nk-gold" hint="Active last 15 min" />
          <Metric Icon={Lightning} label="Active 24h" value={m.active_24h} hint="Seen in past 24 hours" />
          <Metric Icon={Crown} label="Total players" value={m.total_players}
                  hint={`NG ${(m.total_players_ng ?? 0).toLocaleString()} · GH ${(m.total_players_gh ?? 0).toLocaleString()}`} />
          <Metric Icon={Sparkle} label="NK+ subscribers" value={m.nk_plus_subscribers} accent="text-nk-gold" />
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Country War */}
        <section className="lg:col-span-2 space-y-3">
          <p className="eyebrow">Country War · live totals</p>
          <CountryWarRow country="NG" score={cw.NG || 0} other={cw.GH || 0} />
          <CountryWarRow country="GH" score={cw.GH || 0} other={cw.NG || 0} />
        </section>

        {/* Upcoming */}
        <section>
          <p className="eyebrow mb-3">Upcoming events</p>
          <div className="space-y-2">
            <UpcomingRow label="Next Ladder" when={up.next_ladder_at} />
            <UpcomingRow label="NK Daily" when={up.next_daily_at} />
            <UpcomingRow label="Season ends" when={s.ends_at} />
          </div>
        </section>
      </div>

      {/* Today's activity */}
      <section>
        <p className="eyebrow mb-3">Today&apos;s activity</p>
        <div className="grid grid-cols-3 gap-3">
          <Metric Icon={Lightning} label="NK Daily entries" value={t.daily_entries} small />
          <Metric Icon={Trophy} label="Duels played" value={t.duels} small />
          <Metric Icon={Hourglass} label="Survival runs" value={t.survival_runs} small />
        </div>
      </section>

      {/* NKE health card — pool + fallback fire-rate */}
      <NKEHealthCard />

      {/* NKE mass-batch progress (Phase 4 migration + composition) */}
      <MassBatchCard />

      {/* Quick links */}
      <section>
        <p className="eyebrow mb-3">Quick actions</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <QuickLink to="/admin/nke" label="Knowledge Engine" gold />
          <QuickLink to="/admin/championships" label="Championships" gold />
          <QuickLink to="/admin/rewards" label="Prize claims" gold />
          <CommsQuickLink />
        </div>
      </section>
    </div>
  );
}

/* ============= Communications quick link — shows unread auto-post count ============= */
function CommsQuickLink() {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    api.get("/admin/signals?limit=100").then((r) => {
      const rows = r.data?.items || [];
      // "Auto-posted" = created by the release-notes system or pro-tip rotator.
      // Recent = published in the last 7 days AND still active.
      const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
      const autoRecent = rows.filter((s) =>
        (s.created_by === "system:release_notes" || s.created_by === "system:pro_tip") &&
        s.active !== false &&
        s.published_at && new Date(s.published_at).getTime() >= cutoff
      );
      setMeta({ auto: autoRecent.length, total: rows.filter((s) => s.active !== false).length });
    }).catch(() => setMeta({ auto: 0, total: 0 }));
  }, []);
  return (
    <Link
      to="/admin/signals"
      data-testid="admin-comms-quick-link"
      className="nk-card-lift p-4 relative flex items-center justify-between border border-nk-gold/25 hover:border-nk-gold/50 rounded-md bg-nk-gold/[0.04] hover:bg-nk-gold/[0.07] transition-colors"
    >
      <div>
        <p className="text-[10px] uppercase tracking-widest text-nk-gold/70 mb-1">Communications</p>
        <p className="text-sm font-medium text-white">Signals</p>
        {meta && (
          <p className="text-[10px] text-white/50 mt-1 font-mono">
            {meta.total} live · {meta.auto} auto this week
          </p>
        )}
      </div>
      {meta && meta.auto > 0 && (
        <span
          data-testid="admin-comms-auto-badge"
          className="min-w-[18px] h-[18px] px-1.5 rounded-full bg-nk-gold text-nk-charcoal text-[9px] font-mono font-bold flex items-center justify-center"
          title={`${meta.auto} auto-posted signal(s) in the last 7 days`}
        >
          {meta.auto > 9 ? "9+" : meta.auto}
        </span>
      )}
    </Link>
  );
}

/* ============= NKE Health card — pool + fallback fire-rate ============= */
function NKEHealthCard() {
  const [h, setH] = useState(null);
  useEffect(() => {
    let alive = true;
    const tick = () => {
      api.get("/admin/nke/health")
         .then((r) => { if (alive) setH(r.data); })
         .catch(() => {});
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  if (!h) return null;
  const p24 = h.picks_24h || {};
  const legacyPct = p24.legacy_pct ?? 0;
  const nkeLive = h.nke?.live ?? 0;
  const ready = !!h.ready_for_nke_only;

  // Color-code the fallback bar: green if <5%, amber 5-30%, red >30%
  const barColor = legacyPct < 5 ? "bg-nk-gold" : legacyPct < 30 ? "bg-amber-400" : "bg-nk-bad";

  return (
    <section data-testid="admin-nke-health-card">
      <div className="flex items-center justify-between mb-3">
        <p className="eyebrow">NKE health</p>
        <span className={`text-[10px] uppercase tracking-widest font-mono ${
          ready ? "text-nk-gold" : "text-white/40"
        }`}>
          {ready ? "✓ ready for NKE-only" : "Legacy safety-net active"}
        </span>
      </div>
      <div className="nk-card p-5 space-y-4">
        {/* Pool sizes */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MiniStat label="NKE live" value={nkeLive} accent="text-nk-gold" />
          <MiniStat label="NKE pending" value={h.nke?.pending_review} />
          <MiniStat label="Legacy approved" value={h.legacy?.approved} />
          <MiniStat label="Picks · last 24h" value={p24.total} />
        </div>

        {/* Fallback fire-rate */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-sm text-white/80">Legacy fallback fire-rate · 24h</p>
            <p className="text-xs font-mono text-white/60">
              {p24.legacy?.toLocaleString?.() ?? 0} / {p24.total?.toLocaleString?.() ?? 0} · {legacyPct}%
            </p>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div className={`h-full ${barColor} transition-all`} style={{ width: `${Math.max(2, legacyPct)}%` }} />
          </div>
          <p className="text-[11px] text-white/50 mt-1.5">
            {legacyPct < 5
              ? "NKE is serving cleanly — legacy is barely used."
              : legacyPct < 30
              ? "Mostly NKE with some legacy top-up. Keep composing to reduce this."
              : "Legacy is doing most of the work. Run mass batch to enrich NKE."}
          </p>
        </div>
      </div>
    </section>
  );
}

/* ============= NKE mass-batch progress card ============= */
function MassBatchCard() {
  const [p, setP] = useState(null);
  const [runner, setRunner] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/nke/mass-batch/progress").then((r) => setP(r.data)).catch(() => {});
    api.get("/admin/nke/mass-batch/runner-state").then((r) => setRunner(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);

  const start = async () => {
    setBusy(true);
    try {
      await api.post("/admin/nke/mass-batch/start");
      load();
    } finally { setBusy(false); }
  };
  const stop = async () => {
    setBusy(true);
    try {
      await api.post("/admin/nke/mass-batch/stop");
      load();
    } finally { setBusy(false); }
  };

  if (!p) return null;
  const snap = p.snapshot || {};
  const migrated = p.migrated_so_far ?? 0;
  const startLegacy = p.start_legacy ?? 0;
  const pct = p.migration_progress_pct ?? 0;
  const running = !!(runner?.running || p.running);

  return (
    <section data-testid="admin-mass-batch-card">
      <div className="flex items-center justify-between mb-3">
        <p className="eyebrow">NKE mass batch · Phase 4</p>
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase font-mono tracking-widest ${
            running ? "text-nk-gold" : "text-white/40"
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${running ? "bg-nk-gold animate-pulse-dot" : "bg-white/25"}`} />
            {running ? "Running" : (p.last_action || "Idle")}
          </span>
          {running ? (
            <button
              onClick={stop}
              disabled={busy || runner?.stop_requested}
              data-testid="admin-mass-batch-stop"
              className="text-[10px] uppercase tracking-widest text-white/60 hover:text-nk-bad px-2 py-1 border border-white/12 hover:border-nk-bad/40 rounded-sm disabled:opacity-40"
            >
              {runner?.stop_requested ? "Stopping…" : "Stop"}
            </button>
          ) : (
            <button
              onClick={start}
              disabled={busy}
              data-testid="admin-mass-batch-start"
              className="text-[10px] uppercase tracking-widest text-nk-charcoal bg-nk-gold hover:bg-nk-gold/85 px-3 py-1 rounded-sm font-medium disabled:opacity-40"
            >
              Start
            </button>
          )}
        </div>
      </div>

      <div className="nk-card p-5 space-y-4">
        {/* Migration progress bar */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-sm text-white/80">Legacy question migration</p>
            <p className="text-xs font-mono text-white/60">
              {migrated.toLocaleString()} / {startLegacy.toLocaleString()} · {pct}%
            </p>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-nk-gold transition-all" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-[11px] text-white/50 mt-1.5">
            {snap.legacy_remaining ?? 0} legacy questions still to migrate ·{" "}
            {snap.facts ?? 0} knowledge facts active
          </p>
        </div>

        {/* Live vs pending variants */}
        <div className="grid grid-cols-3 gap-3">
          <MiniStat label="Variants live" value={snap.variants_live} accent="text-nk-gold" />
          <MiniStat label="Pending review" value={snap.variants_pending} />
          <MiniStat label="Total variants" value={snap.variants_total} />
        </div>

        {p.batch_id && (
          <p className="text-[10px] text-white/35 font-mono">
            Batch {p.batch_id} · last event {p.last_action || "—"} @ {formatWhen(p.last_at)}
          </p>
        )}
      </div>
    </section>
  );
}

function MiniStat({ label, value, accent = "" }) {
  return (
    <div className="border border-white/8 rounded-sm px-3 py-2.5">
      <p className={`font-display text-2xl font-medium tabular-nums ${accent || "text-white"}`}>
        {(value ?? 0).toLocaleString()}
      </p>
      <p className="text-[10px] text-white/50 uppercase tracking-wider mt-0.5">{label}</p>
    </div>
  );
}

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    const t = new Date(iso);
    const now = new Date();
    const mins = Math.round((now - t) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
    return t.toLocaleDateString();
  } catch { return iso; }
}


function Heading({ season }) {
  return (
    <header className="mb-6">
      <div className="flex items-center gap-2 mb-1">
        <Gauge size={14} className="text-nk-gold" />
        <p className="font-mono text-[10px] text-white/40 uppercase tracking-[0.18em]">Command Center</p>
      </div>
      <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight">Dashboard</h1>
      {season && (
        <p className="text-sm text-white/60 mt-2">
          Season <span className="text-nk-text font-mono">{season.number}</span> · Day{" "}
          <span className="text-nk-text font-mono">{season.day}</span> / 30 ·{" "}
          <span className="capitalize">{(season.state || "").toLowerCase().replace("_", " ")}</span>
        </p>
      )}
    </header>
  );
}

function SeasonBanner({ season, top }) {
  if (!season) return null;
  const remaining = Math.max(0, 30 - (season.day || 0));
  const pct = Math.min(100, ((season.day || 0) / 30) * 100);
  return (
    <div className="nk-card p-5 sm:p-6 border-nk-gold/20 bg-nk-gold/[0.03]">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] text-nk-gold uppercase tracking-[0.18em] mb-1">
            Season {season.number} · in progress
          </p>
          <p className="font-display text-2xl sm:text-3xl font-medium">
            Day {season.day} of 30
            <span className="text-white/60 text-base ml-3">· {remaining} days remaining</span>
          </p>
        </div>
        {top && (
          <Link to={`/profile/${top.user_id}`} className="flex items-center gap-3 px-3 py-2 border border-nk-line rounded-sm hover:border-nk-gold transition-colors">
            <AvatarIcon id={top.avatar} size={28} />
            <div className="min-w-0">
              <p className="text-[10px] text-white/40 font-mono uppercase tracking-wider">Top player</p>
              <p className="text-sm font-medium flex items-center gap-1.5">
                {top.username}
                <CountryFlag country={top.country} size={11} />
              </p>
            </div>
            <span className="font-mono text-sm text-nk-gold">{(top.season_score || 0).toLocaleString()}</span>
          </Link>
        )}
      </div>
      <div className="mt-5 h-1 bg-nk-line rounded-full overflow-hidden">
        <div className="h-full bg-nk-gold transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Metric({ Icon, label, value, accent = "text-nk-text", hint, small = false }) {
  return (
    <div className="nk-card p-4">
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon size={13} weight="duotone" className="text-white/60" />}
        <p className="text-[11px] text-white/60 uppercase tracking-wider">{label}</p>
      </div>
      <p className={`font-display ${small ? "text-2xl" : "text-3xl"} font-medium ${accent}`}>
        {(value ?? 0).toLocaleString()}
      </p>
      {hint && <p className="text-[10px] text-white/40 mt-1">{hint}</p>}
    </div>
  );
}

function CountryWarRow({ country, score, other }) {
  const total = score + other;
  const pct = total > 0 ? (score / total) * 100 : 50;
  const leading = score > other;
  return (
    <div className="nk-card p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <CountryFlag country={country} size={16} />
          <span className="font-display text-lg font-medium">{country === "NG" ? "Nigeria" : "Ghana"}</span>
          {leading && <span className="text-[10px] text-nk-gold font-mono uppercase tracking-wider">leading</span>}
        </div>
        <span className="font-mono text-lg text-nk-gold">{score.toLocaleString()}</span>
      </div>
      <div className="h-1.5 bg-nk-line rounded-full overflow-hidden">
        <div className={`h-full ${leading ? "bg-nk-gold" : "bg-white/20"} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function UpcomingRow({ label, when }) {
  const dt = when ? new Date(when) : null;
  const diffMs = dt ? dt.getTime() - Date.now() : null;
  let display = "—";
  if (diffMs !== null) {
    if (diffMs < 0) display = "Started";
    else if (diffMs < 60 * 60 * 1000) display = `${Math.ceil(diffMs / 60000)} min`;
    else if (diffMs < 24 * 60 * 60 * 1000) display = `${Math.floor(diffMs / 3600000)}h ${Math.floor((diffMs % 3600000) / 60000)}m`;
    else display = `${Math.ceil(diffMs / (24 * 3600000))} days`;
  }
  return (
    <div className="nk-card px-4 py-3 flex items-center justify-between">
      <p className="text-sm text-white/60">{label}</p>
      <p className="font-mono text-sm text-nk-text">{display}</p>
    </div>
  );
}

function QuickLink({ to, label, gold }) {
  return (
    <Link
      to={to}
      className={`nk-card px-4 py-3 flex items-center justify-between text-sm transition-colors group ${
        gold ? "hover:border-nk-gold/60 text-nk-gold/90" : "hover:border-nk-gold text-white/60 hover:text-nk-text"
      }`}
    >
      <span>{label}</span>
      <ArrowRight size={12} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
    </Link>
  );
}
